/**
 * Public Form Page - Renders a published agent form interface for end users
 * Path: /form/:agentId/:slug
 * No authentication required
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormRenderer } from "@/components/agents/interfaces/form-renderer";
import { api } from "@/utils/api-client";
import type { AgentUiRecord } from "@/types/agent-ui-types";
import { usePageTitle } from "@/hooks/use-page-title";

/** Shape of the public form API response */
interface PublicFormData extends AgentUiRecord {
  agentInfo: {
    name: string;
    description: string | null;
    avatar: string | null;
  } | null;
}

/** Shape of the submission API response */
interface SubmissionResult {
  chatId: string;
  response: {
    text: string;
    attachments: unknown[];
  };
}

/**
 * Public form page component
 * Fetches the published form, renders it, handles submission, shows result
 */
export function PublicFormPage() {
  const { agentId, slug } = useParams<{ agentId: string; slug: string }>();
  const pageTitle = usePageTitle("Form", "Submit a form");

  const [formData, setFormData] = useState<PublicFormData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<SubmissionResult | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  /** Trigger form submission from the footer button */
  const handleFormSubmit = () => {
    const formElement = formRef.current;
    if (formElement) {
      // Trigger the form's submit event
      formElement.requestSubmit();
    }
  };

  /** Fetch the published form on mount */
  const fetchForm = useCallback(async () => {
    if (!agentId || !slug) return;

    try {
      const response = await api.get(
        `/public/agents/${agentId}/uis/${slug}`,
        { requiresAuth: false }
      );
      setFormData(response.data);
    } catch {
      setError("This form is not available or has been unpublished.");
    } finally {
      setIsLoading(false);
    }
  }, [agentId, slug]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  /** Handle form submission */
  const handleSubmit = async (values: Record<string, unknown>) => {
    if (!agentId || !slug) return;

    setIsSubmitting(true);
    try {
      const response = await api.post(
        `/public/agents/${agentId}/uis/${slug}/submit`,
        { values },
        { requiresAuth: false, showErrorToast: false }
      );
      setResult(response.data);
    } catch {
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageShell>
        {pageTitle}
        <Card className="w-full max-w-lg mx-auto flex flex-col h-full">
          <CardHeader className="text-center pb-4 shrink-0">
            <Skeleton className="h-12 w-12 rounded-full mx-auto mb-2" />
            <Skeleton className="h-5 w-48 mx-auto" />
            <Skeleton className="h-4 w-64 mx-auto mt-2" />
          </CardHeader>
          <CardContent className="space-y-4 overflow-y-auto flex-1 min-h-0">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (error || !formData) {
    return (
      <PageShell>
        {pageTitle}
        <Card className="w-full max-w-lg mx-auto">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{error ?? "Form not found"}</p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {pageTitle}
      <Card className="w-full max-w-lg mx-auto flex flex-col h-full overflow-hidden">
        {/* Agent header - fixed */}
        <CardHeader className="text-center pb-4 shrink-0 border-b">
          {formData.agentInfo?.avatar && (
            <Avatar className="h-12 w-12 mx-auto mb-2">
              <AvatarImage
                src={formData.agentInfo.avatar}
                alt={formData.agentInfo.name}
              />
              <AvatarFallback>
                {formData.agentInfo.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          )}
          <h1 className="text-lg font-semibold">{formData.name}</h1>
          {formData.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {formData.description}
            </p>
          )}
          {formData.agentInfo && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Powered by {formData.agentInfo.name}
            </p>
          )}
        </CardHeader>

        {/* Form content - scrollable */}
        <CardContent className="flex-1 overflow-y-auto p-6">
          {result ? (
            <SubmissionSuccess result={result} onReset={() => setResult(null)} />
          ) : (
            <FormRenderer
              schema={formData.formSchema}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              formRef={formRef}
            />
          )}
        </CardContent>

        {/* Submit button footer - fixed */}
        {formData.formSchema.submitButton && !result && (
          <div className="p-4 border-t bg-background shrink-0">
            <Button
              type="button"
              disabled={isSubmitting}
              className="w-full"
              onClick={handleFormSubmit}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {formData.formSchema.submitButton.loadingLabel ?? "Submitting..."}
                </>
              ) : (
                formData.formSchema.submitButton.label
              )}
            </Button>
          </div>
        )}
      </Card>
    </PageShell>
  );
}

/** Page wrapper with centered layout and branding */
function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-screen w-screen bg-muted/30 flex flex-col overflow-hidden p-4">
      {/* Centered container */}
      <div className="flex-1 flex flex-col overflow-hidden max-w-2xl mx-auto w-full">
        {children}
      </div>
      {/* Footer - fixed at bottom */}
      <p className="text-center text-xs text-muted-foreground py-2 shrink-0">
        Powered by Taqat AI
      </p>
    </div>
  );
}

/** Success state after form submission */
function SubmissionSuccess({
  result,
  onReset,
}: {
  result: SubmissionResult;
  onReset: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="text-center">
        <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
        <h2 className="text-base font-semibold">Submission Received</h2>
      </div>

      <div className="rounded-lg border bg-muted/30 p-4">
        <p className="text-sm whitespace-pre-wrap">{result.response.text}</p>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="text-sm text-primary hover:underline mx-auto block"
      >
        Submit another response
      </button>
    </div>
  );
}

PublicFormPage.displayName = "PublicFormPage";
